// @flow
import React, { useMemo, useState, useCallback, useEffect, type Node } from 'react';
import isEmpty from 'ramda/es/isEmpty';
import prop from 'ramda/es/prop';
import find from 'ramda/es/find';
import propEq from 'ramda/es/propEq';

import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link';
import PlainIcon, { iconTypes } from 'app/common/ui-next/plain-icon';

import { useSolutionsData, useUploadingFiles, type SolutionsRequestParams, type SolutionCreateRequestParams } from '../SolutionsData';
import { VerificationsData } from '../VerificationsData';
import SolutionModal from './SolutionModal';
import SolutionIcon from './SolutionIcon';
import CreateSolutionModal from './CreateSolutionModal';


type Props = {
  className?: string,
  scheduleId?: number,
  assignmentIds?: Array<number>,
  studentId?: number,
}

const Solutions = ({
  className,
  scheduleId,
  assignmentIds,
  studentId,
}: Props): Node => {
  const [currentSulutionId, setCurrentSulutionId] = useState<number | null>(null);
  const toggleModal = useCallback((solutionId: number | null) => () => {
    setCurrentSulutionId(state => (state ? null : solutionId));
  }, []);

  // после создания решения, его id сохраняется здесь
  // на следующем цикле оно будет перенесено в currentSulutionId для показа окна с решениями
  const [readyToShowSolutionId, setReadyToShowSolutionId] = useState<number | null>(null);


  const [creatingSolution, setCreatingSolution] = useState(false);

  const handleCreateSolution = useCallback(() => {
    setCreatingSolution(true);
  }, []);

  const handleCreateSolutionSucces = useCallback((solutionId: number) => {
    setReadyToShowSolutionId(solutionId);
    setCreatingSolution(false);
  }, []);

  const handleCreateSolutionFail = useCallback(() => {
    setCreatingSolution(false);
  }, []);


  // параметры для запроса решений
  const requestParams: SolutionsRequestParams | null = useMemo(() => (
    scheduleId && studentId
      ? {
        schedule_id: [scheduleId],
        student_id: [studentId],
      } : null
  ), [scheduleId, studentId]);

  // параметры для запроса создания решений
  const requestCreateSolutionsParams: SolutionCreateRequestParams | null = useMemo(() => {
    if (assignmentIds && !isEmpty(assignmentIds) && assignmentIds[0] && scheduleId && studentId) {
      return {
        assignment_id: assignmentIds[0],
        schedule_id: scheduleId,
        student_id: studentId,
      };
    }
    if (scheduleId && studentId) {
      return {
        schedule_id: scheduleId,
        student_id: studentId,
      };
    }
    return null;
  }, [scheduleId, assignmentIds, studentId]);

  const [[solutions, loadingSolutions], actions] = useSolutionsData(requestParams);
  // console.log(loadingSolutions, requestCreateSolutionsParams);
  // // console.log(requestParams ? solutions : '');
  // // console.log(requestParams || '');
  const [
    uploadingFiles,
    failedFiles,
    uploadFiles,
    deleteFile,
    uploading,
  ] = useUploadingFiles(actions.postFile, currentSulutionId);

  // readyToShowSolutionId появляется сразу после создания решения
  // нужен цикл рендера, чтоб в solution точно появилось это решение
  // после чего можно включать окно с решениями
  useEffect(() => {
    if (readyToShowSolutionId && find(propEq('id', readyToShowSolutionId), solutions)) {
      setCurrentSulutionId(readyToShowSolutionId);
      setReadyToShowSolutionId(null);
    }
  }, [solutions, readyToShowSolutionId]);


  // не выводится ничего
  // 1. в процессе загрузки решений для элементов где есть requestParams
  if (loadingSolutions && requestParams) {
    return null;
  }

  return (
    <VerificationsData>
      {/* данные в контекст по проверкам прикрепленных решений
      используются в SolutionIcon при отображении типа иконки
      так же в окне отображения решений SolutionModal */}

      {/* Все решения которые есть для этого задания, обычно это одно решение */}
      {solutions.map(({ id, attached_files: files = [] }) => (
        <PlainButtonAsLink
          key={id}
          className={className}
          onClick={toggleModal(id)}
        >
          <SolutionIcon
            solutionId={id}
            attachedFilesIds={files.map(prop('id'))}
          />
        </PlainButtonAsLink>
      ))}

      {requestCreateSolutionsParams
        && isEmpty(solutions)
        && <PlainButtonAsLink
          className={className}
          onClick={handleCreateSolution}
        >
          <PlainIcon
            type={iconTypes.UPLOAD_FILE}
            width={20}
            height="auto"
          />
        </PlainButtonAsLink>}

      {/* Окно просмотра решения */}
      {currentSulutionId
        && <SolutionModal
          onClose={toggleModal(null)}
          onDeleteAttachedFile={actions.deleteAttachedFile}
          onDeleteProcessingFile={deleteFile}
          onAddFiles={uploadFiles}
          solution={find(propEq('id', currentSulutionId), solutions)}
          uploadingFiles={uploadingFiles}
          failedFiles={failedFiles}
          uploading={uploading}
        />}

      {/* Окно создания решения */}
      {creatingSolution
        && requestCreateSolutionsParams
        && <CreateSolutionModal
          onCreateSolution={actions.createSolution}
          createParams={requestCreateSolutionsParams}
          onCreateSuccess={handleCreateSolutionSucces}
          onCreateFail={handleCreateSolutionFail}
        />}
    </VerificationsData>
  );
};

export default Solutions;
